import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchWorkoutTemplatesApi,
  deleteWorkoutTemplateApi,
  createWorkoutTemplateApi,
  fetchWorkoutTemplateByIdApi,
  filterWorkoutTemplatesApi,
  updateWorkoutTemplateApi,
} from "../../api/workoutApi";


export const fetchWorkoutTemplates = createAsyncThunk(
  "workouts/fetchWorkoutTemplates",
  async ({ coachId, pageNumber, pageSize }, { rejectWithValue }) => {
    try {
      return await fetchWorkoutTemplatesApi({ coachId, pageNumber, pageSize });
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch workout templates"
      );
    }
  }
);

// DELETE workout template
export const deleteWorkoutTemplate = createAsyncThunk(
  "workouts/deleteWorkoutTemplate",
  async (templateId, { rejectWithValue }) => {
    try {
      return await deleteWorkoutTemplateApi(templateId);
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to delete workout template"
      );
    }
  }
);

export const createWorkoutTemplate = createAsyncThunk(
  "workouts/createWorkoutTemplate",
  async (workoutData, { rejectWithValue }) => {
    try {
      return await createWorkoutTemplateApi(workoutData);
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to create workout template"
      );
    }
  }
);

export const fetchWorkoutTemplateById = createAsyncThunk(
  "workouts/fetchWorkoutTemplateById",
  async (templateId, { rejectWithValue }) => {
    try {
      return await fetchWorkoutTemplateByIdApi(templateId);
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch template details"
      );
    }
  }
);

export const filterWorkoutTemplates = createAsyncThunk(
  "workouts/filterWorkoutTemplates",
  async ({ coachId, name }, { rejectWithValue }) => {
    try {
      return await filterWorkoutTemplatesApi({ coachId, name });
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to filter workout templates"
      );
    }
  }
);

export const updateWorkoutTemplate = createAsyncThunk(
  "workouts/updateWorkoutTemplate",
  async ({ templateId, workoutData }, { rejectWithValue }) => {
    try {
      return await updateWorkoutTemplateApi(templateId, workoutData);
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to update workout template"
      );
    }
  }
);

const workoutSlice = createSlice({
  name: "workouts",
  initialState: {
    workoutTemplates: [],
    totalCount: 0,
    loading: false,
    error: null,
    pagination: { current: 1, pageSize: 10 },
    toastConfig: null,
  },
  reducers: {
    updatePagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    resetToastConfig: (state) => {
      state.toastConfig = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWorkoutTemplates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWorkoutTemplates.fulfilled, (state, action) => {
        state.loading = false;
        state.workoutTemplates = action.payload.workoutTemplates;
        state.totalCount = action.payload.totalCount;
      })
      .addCase(fetchWorkoutTemplates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // DELETE case
      .addCase(deleteWorkoutTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteWorkoutTemplate.fulfilled, (state, action) => {
        state.loading = false;
        state.toastConfig = {
          type: "success",
          message: "Workout Template deleted successfully.",
          heading: "Success",
        };

        // Remove deleted template from state
        state.workoutTemplates = state.workoutTemplates.filter(
          (template) => template.id !== action.meta.arg
        );

        state.totalCount -= 1;
      })
      .addCase(deleteWorkoutTemplate.rejected, (state, action) => {
        state.loading = false;
        state.toastConfig = {
          type: "error",
          message: action.payload || "Failed to delete workout template.",
          heading: "Error",
        };
      })
      .addCase(createWorkoutTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createWorkoutTemplate.fulfilled, (state, action) => {
        state.loading = false;
        state.toastConfig = {
          type: "success",
          message: "Workout Template created successfully.",
          heading: "Success",
        };
      })
      .addCase(createWorkoutTemplate.rejected, (state, action) => {
        state.loading = false;
        state.toastConfig = {
          type: "error",
          message: action.payload || "Failed to create workout template.",
          heading: "Error",
        };
      })
      .addCase(fetchWorkoutTemplateById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWorkoutTemplateById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedTemplate = action.payload;
      })
      .addCase(fetchWorkoutTemplateById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(filterWorkoutTemplates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(filterWorkoutTemplates.fulfilled, (state, action) => {
        state.loading = false;
        state.workoutTemplates = action.payload.workoutTemplates;
        state.totalCount = action.payload.totalCount;
      })
      .addCase(filterWorkoutTemplates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateWorkoutTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateWorkoutTemplate.fulfilled, (state) => {
        state.loading = false;
        state.toastConfig = {
          type: "success",
          message: "Workout Template updated successfully.",
          heading: "Success",
        };
      })
      .addCase(updateWorkoutTemplate.rejected, (state, action) => {
        state.loading = false;
        state.toastConfig = {
          type: "error",
          message: action.payload || "Failed to update workout template.",
          heading: "Error",
        };
      });
  },
});

export const { updatePagination, resetToastConfig } = workoutSlice.actions;
export default workoutSlice.reducer;
